import DeviceModelExtractor from '@/services/conviva/DeviceModelExtractor';
import { DeviceMetaType, PlatformType } from '@/services/conviva/conviva.types';

function resolveBrand(platform: PlatformType): string {
  switch (platform) {
    case PlatformType.Tizen:
      return 'Samsung';
    case PlatformType.WebOS:
      return 'LG';
    case PlatformType.Vidaa:
      return 'Hisense';
    case PlatformType.PS5:
      return 'Sony';
    default:
      return 'Unknown';
  }
}

function resolveOsName(platform: PlatformType): string {
  switch (platform) {
    case PlatformType.Tizen:
      return 'Tizen';
    case PlatformType.WebOS:
      return 'webOS';
    case PlatformType.Vidaa:
      return 'VIDAA';
    case PlatformType.PS5:
      return 'Orbis OS';
    default:
      return 'Web';
  }
}

class DeviceDetector {
  deviceMetadata: DeviceMetaType = {
    platform: PlatformType.Web,
    osVersion: 'NA',
    osName: 'Web',
    deviceModel: 'NA',
    brand: 'Unknown',
    manufacturer: 'Unknown',
    connectionType: 'NA',
  };

  /** Resolves when platform data has been populated. Await before reading deviceMetadata in async paths. */
  readonly ready: Promise<void>;

  constructor() {
    this.ready = this.getPlatformData().catch(err => {
      console.error('[conviva] Failed to get platform data:', err);
    }) as Promise<void>;
  }

  private normalizeConnectionType(rawType?: string): string {
    if (!rawType || typeof rawType !== 'string') {
      return 'Unknown';
    }
    const value = rawType.toLowerCase();
    if (value.includes('wifi')) {
      return 'WiFi';
    }
    if (value.includes('ethernet') || value.includes('wired')) {
      return 'Ethernet';
    }
    if (value.includes('cell') || value.includes('4g') || value.includes('5g')) {
      return 'Cellular';
    }
    if (value.includes('wimax')) {
      return 'WiMax';
    }
    return 'WiFi'; // @TODO: fallback
  }

  private detectConnectionType(platform: PlatformType): string {
    try {
      if (typeof navigator !== 'undefined') {
        const connection =
          (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
        if (connection?.type) {
          return this.normalizeConnectionType(connection.type);
        }
        if (connection?.effectiveType) {
          return this.normalizeConnectionType(connection.effectiveType);
        }
      }

      if (typeof window !== 'undefined' && platform === PlatformType.Tizen) {
        const network = (window as any).webapis?.network;
        if (network?.getActiveConnectionType) {
          const type = network.getActiveConnectionType();
          const networkTypeEnum = network?.NetworkType || {};
          if (type === networkTypeEnum.WIFI) {
            return 'WiFi';
          }
          if (type === networkTypeEnum.ETHERNET) {
            return 'Ethernet';
          }
          if (type === networkTypeEnum.WIMAX) {
            return 'WiMax';
          }
          if (type === networkTypeEnum.DISCONNECTED) {
            return 'Offline';
          }
        }
      }
    } catch (error) {
      console.warn('[conviva] Unable to determine connection type', error);
    }

    return 'WiFi';
  }

  async getPlatformData(): Promise<void> {
    const platform = (process.env.VUE_APP_PLATFORM || PlatformType.Web) as PlatformType;

    // Set platform immediately (synchronous) so it's available before async operations complete
    this.deviceMetadata.platform = platform;

    let deviceModel: string;
    let osVersion = 'NA';

    // For web/browser platforms (including PS5), skip DeviceModelExtractor entirely to avoid any TV SDK calls
    if (platform === PlatformType.Web || platform === PlatformType.PS5) {
      if (platform === PlatformType.PS5) {
        deviceModel = 'PlayStation 5';
        osVersion = 'NA';
      } else {
        // Get browser info for web emulator
        const userAgent = navigator.userAgent;
        if (userAgent.includes('Chrome')) {
          deviceModel = 'Web_Chrome';
        } else if (userAgent.includes('Firefox')) {
          deviceModel = 'Web_Firefox';
        } else if (userAgent.includes('Safari')) {
          deviceModel = 'Web_Safari';
        } else if (userAgent.includes('Edge')) {
          deviceModel = 'Web_Edge';
        } else {
          deviceModel = 'Web_Browser';
        }

        // Get OS version for web
        if (navigator.userAgent.includes('Mac OS X')) {
          osVersion = 'macOS';
        } else if (navigator.userAgent.includes('Windows')) {
          osVersion = 'Windows';
        } else if (navigator.userAgent.includes('Linux')) {
          osVersion = 'Linux';
        } else {
          osVersion = 'Web';
        }
      }
    } else {
      // Only create DeviceModelExtractor for TV platforms
      const deviceModelExtractor = new DeviceModelExtractor({});

      // Only initialize webOS if we're actually on webOS platform
      const initializeWebOS = platform === PlatformType.WebOS;

      // For TV platforms, use DeviceModelExtractor
      const deviceModelResult = deviceModelExtractor.getDeviceModel(platform, { initializeWebOS });
      deviceModel = deviceModelResult instanceof Promise ? await deviceModelResult : deviceModelResult;

      // Fallback if extraction failed
      if (!deviceModel || deviceModel === 'UNKNOWN_PLATFORM') {
        deviceModel = `${platform}_Unknown`;
      }

      // Get OS version for TV platforms
      if (platform === PlatformType.WebOS) {
        osVersion = deviceModelExtractor.getWebosVersion();
      } else if (platform === PlatformType.Tizen) {
        osVersion = deviceModelExtractor.getTizenVersion();
      } else if (platform === PlatformType.Vidaa) {
        osVersion = deviceModelExtractor.getHisenseVersion();
      }
    }

    const connectionType = this.detectConnectionType(platform);
    const brand = resolveBrand(platform);
    this.deviceMetadata = {
      platform,
      osVersion,
      osName: resolveOsName(platform),
      deviceModel,
      brand,
      manufacturer: brand,
      connectionType,
    };
  }
}

export const deviceDetector = new DeviceDetector();
